import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import { SectionHeading, tone } from '@/components/agents/primitives';

import { imageUrl } from './assets';
import { useInteriorLandingT, usePageVars, useRoomVars } from './kit';
import { roomPath, TOOL_PAGES } from './pages';
import { INTERIOR_ROOM_GROUPS, INTERIOR_ROOMS } from './rooms';
import type { InteriorLandingPage } from './types';

/**
 * Every room and job page, linked with the phrase people search for
 * ("AI kitchen design"). The current page is marked, not linked.
 */
export default function RoomDirectory({ page }: { page: InteriorLandingPage }) {
  const { t } = useInteriorLandingT();
  const roomVars = useRoomVars();
  const vars = usePageVars(page);

  return (
    <section id='rooms' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <SectionHeading
          title={t('Design any space,')}
          tail={t('inside and out.')}
          lead={t(
            '{{rooms}} room types, from kitchens and nurseries to cafés, salons and backyards.',
            vars,
          )}
        />

        <div className='mt-12 grid grid-cols-2 gap-3 lg:grid-cols-4'>
          {TOOL_PAGES.map((tool) => {
            const current = tool.path === page.path;
            const body = (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageUrl(tool.image, 320)}
                  alt=''
                  width={56}
                  height={56}
                  loading='lazy'
                  decoding='async'
                  className='h-14 w-14 flex-none rounded-xl object-cover'
                />
                <span
                  className={clsxm(
                    'text-[15px] font-semibold leading-snug',
                    tone.ink,
                  )}
                >
                  {t(tool.label)}
                </span>
              </>
            );
            const className = clsxm(
              'flex items-center gap-3 rounded-2xl bg-white p-2.5 pe-4 ring-1 ring-[#0f0f28]/[0.07] dark:bg-white/[0.04] dark:ring-white/10',
              current
                ? 'ring-2 ring-primary-500 dark:ring-primary-400'
                : 'transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_32px_-20px_rgba(22,12,90,0.5)]',
            );
            return current ? (
              <div key={tool.path} className={className} aria-current='page'>
                {body}
              </div>
            ) : (
              <Link key={tool.path} href={tool.path} className={className}>
                {body}
              </Link>
            );
          })}
        </div>

        <div className='mt-14 grid gap-12 lg:grid-cols-3 lg:gap-10'>
          {INTERIOR_ROOM_GROUPS.map((group) => (
            <div key={group.id}>
              <h3
                className={clsxm(
                  'border-b border-[#0f0f28]/[0.08] pb-3 text-sm font-semibold uppercase tracking-wide dark:border-white/[0.08]',
                  tone.quiet,
                )}
              >
                {t(group.label)}
              </h3>
              <ul className='mt-4 flex flex-wrap gap-x-1 gap-y-1.5'>
                {INTERIOR_ROOMS.filter((room) => room.group === group.id).map(
                  (room) => {
                    const path = roomPath(room.slug);
                    const label = t(room.name);
                    const current = path === page.path;
                    return (
                      <li key={room.slug}>
                        {current ? (
                          <span
                            aria-current='page'
                            className='inline-block rounded-full bg-primary-600 px-3 py-1 text-[14px] font-medium text-white'
                          >
                            {label}
                          </span>
                        ) : (
                          <Link
                            href={path}
                            title={t('AI {{room}} Design', roomVars(room.name))}
                            className={clsxm(
                              'inline-block rounded-full px-3 py-1 text-[14px] transition-colors hover:bg-white hover:text-primary-700 dark:hover:bg-white/[0.06] dark:hover:text-primary-300',
                              tone.body,
                            )}
                          >
                            {label}
                          </Link>
                        )}
                      </li>
                    );
                  },
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
